import React from "react";

import Box from "components/common/Box";
import Text from "components/common/Text";

import { marketPulseTheme as theme } from "./marketPulseTheme";

const ChevronIcon = ({ direction = "down" }) => (
  <Box
    component="svg"
    viewBox="0 0 15 8"
    aria-hidden
    sx={{
      width: 15,
      height: 7.4,
      flexShrink: 0,
      transform: direction === "up" ? "rotate(180deg)" : "rotate(0deg)",
      transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
      display: "block",
    }}
  >
    <path
      d="M1.2 1.2 L7.5 6.6 L13.8 1.2"
      fill="none"
      stroke="#F5F5F5"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

const MarketPulseToggleButton = ({
  title,
  subtitle,
  isExpanded,
  onClick,
  placement = "top",
  sx = {},
}) => {
  const isBottom = placement === "bottom";
  const chevronDirection = isExpanded || isBottom ? "up" : "down";

  return (
    <Box
      sx={{
        display: "inline-flex",
        justifyContent: "center",
        width: theme.pillWidth,
        minWidth: theme.pillWidth,
        height: 74,
        zIndex: 6,
        ...(isBottom
          ? {
              /* Figma 6776:47955 — centered on outer dark blue card */
              position: "absolute",
              left: "50%",
              bottom: -40,
              transform: "translateX(-50%)",
            }
          : {
              /* Figma 6776:47689 — centered when collapsed */
              position: "relative",
              mx: "auto",
            }),
        ...sx,
      }}
    >
      {/* Figma 6731:44166 — navy 3D base */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: 11,
          height: 63,
          borderRadius: theme.pillBaseRadius,
          backgroundColor: theme.pillBaseColor,
          boxShadow: theme.pillBaseInset,
          pointerEvents: "none",
        }}
      />

      {/* Figma 6731:44167 — gradient face */}
      <Box
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onClick();
          }
        }}
        sx={{
          position: "relative",
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          overflow: "hidden",
          width: "100%",
          height: theme.pillFaceHeight,
          pt: "8px",
          pb: "12px",
          px: "18px",
          borderRadius: theme.pillFaceRadius,
          background: theme.pillFaceGradient,
          border: isBottom ? "1.5px solid rgba(255, 255, 255, 0.35)" : "none",
          borderTop: isBottom ? "1.5px solid rgba(255, 255, 255, 0.55)" : undefined,
          boxShadow: theme.pillFaceInset,
          cursor: "pointer",
          userSelect: "none",
          transition: "filter 0.15s ease",
          "&:hover": {
            filter: "brightness(1.03)",
          },
          "&:active": {
            filter: "brightness(0.97)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "6px",
            width: "100%",
          }}
        >
          <Box
            sx={{
              width: 182,
              maxWidth: "88%",
              height: "1px",
              backgroundColor: theme.pillSeparator,
              flexShrink: 0,
            }}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Text
                sx={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 600,
                  fontSize: "18px",
                  lineHeight: 1,
                  letterSpacing: "-0.54px",
                  color: theme.pillTitleColor,
                  whiteSpace: "nowrap",
                }}
              >
                {title}
              </Text>
              <ChevronIcon direction={chevronDirection} />
            </Box>

            <Text
              sx={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: 1,
                letterSpacing: "-0.42px",
                color: theme.pillSubtitleColor,
                whiteSpace: "nowrap",
                textAlign: "center",
              }}
            >
              {subtitle}
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MarketPulseToggleButton;
